import { useEffect, useRef, useState } from 'react';

interface Props {
  title: string;
  energy?: number | undefined;
  schedule?: string | undefined;
  xp?: number | undefined;
  done: boolean;
  disabled?: boolean | undefined;
  onToggle: () => void;
  onSkip?: (() => void) | undefined;
  onEdit?: (() => void) | undefined;
  onDelete?: (() => void) | undefined;
  accent: 'habit' | 'task' | 'ach';
}

const ACCENT = {
  habit: 'border-habit-500/40 hover:border-habit-400',
  task: 'border-task-500/40 hover:border-task-400',
  ach: 'border-ach-500/40 hover:border-ach-400',
} as const;

const ENERGY_TINT = {
  habit: 'bg-habit-500/20 text-habit-400',
  task: 'bg-task-500/20 text-task-400',
  ach: 'bg-ach-500/20 text-ach-400',
} as const;

export function KanbanCard({
  title, energy, schedule, xp, done, disabled,
  onToggle, onSkip, onEdit, onDelete, accent,
}: Props) {
  return (
    <li
      className={`group flex items-center gap-3 rounded-lg border bg-bg-raised p-3 transition ${ACCENT[accent]} ${
        done ? 'opacity-60' : ''
      }`}
    >
      <input
        type="checkbox"
        checked={done}
        disabled={disabled}
        onChange={onToggle}
        className="h-5 w-5 shrink-0 accent-brand-500 cursor-pointer"
      />
      {energy != null && (
        <span className={`shrink-0 rounded-md px-2 py-0.5 font-pixel text-[10px] ${ENERGY_TINT[accent]}`}>
          ⚡{energy}
        </span>
      )}
      <div className="flex-1 min-w-0">
        <p className="truncate font-medium text-neutral-100">{title}</p>
        {(schedule || xp != null) && (
          <p className="truncate text-xs text-neutral-400">
            {schedule}
            {xp != null && <span className="ml-2 font-pixel text-xp-bar">+{xp} XP</span>}
          </p>
        )}
      </div>
      {!done && onSkip && (
        <button
          onClick={onSkip}
          className="shrink-0 rounded px-2 py-1 text-[11px] text-neutral-500 hover:bg-bg-elevated hover:text-neutral-200"
          title="Atla"
        >
          atla
        </button>
      )}
      {(onEdit || onDelete) && <CardMenu onEdit={onEdit} onDelete={onDelete} />}
    </li>
  );
}

function CardMenu({ onEdit, onDelete }: { onEdit?: (() => void) | undefined; onDelete?: (() => void) | undefined }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="rounded px-2 py-1 text-neutral-500 hover:bg-bg-elevated hover:text-neutral-200"
        title="Menü"
      >
        ⋮
      </button>
      {open && (
        <div className="absolute right-0 top-full z-10 mt-1 w-32 overflow-hidden rounded-md border border-border-soft bg-bg-raised shadow-xl">
          {onEdit && (
            <button onClick={() => { setOpen(false); onEdit(); }}
                    className="block w-full px-3 py-1.5 text-left text-sm text-neutral-200 hover:bg-bg-elevated">
              Düzenle
            </button>
          )}
          {onDelete && (
            <button onClick={() => { setOpen(false); onDelete(); }}
                    className="block w-full border-t border-border-soft px-3 py-1.5 text-left text-sm text-red-400 hover:bg-bg-elevated">
              Sil
            </button>
          )}
        </div>
      )}
    </div>
  );
}
